import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconCar,
  IconChevronLeft,
  IconChevronRight,
  IconCircleCheck,
  IconMail,
  IconMaximize,
  IconPhone,
  IconSparkles,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { NapakaApi, sporocilo } from '../api/napaka';
import { podatki, type Shema } from '../api/odjemalec';
import { cenaOglasa } from '../oblika/oblika';
import { Kartica, Podatki } from '../postavitev/Podatki';
import { barve } from '../tema';
import { javniApi, prepreciIndeksiranje, slikaUrl, telefonUrl, vgrajeno } from './javno';
import { Diaprojekcija } from './Diaprojekcija';
import { NiPonudbe } from './NiPonudbe';
import { PostavitevPonudbe } from './Postavitev';
import { usePoteg } from './poteg';

type Vozilo = Shema['JavnoVozilo'];
type Prodajalec = Shema['JavniProdajalec'];

/**
 * Javna stran vozila (brez prijave, koda QR na oglasu vodi sem): na vrhu »Celotna zaloga«, slike, naziv in cena,
 * podatki in opis, klic in povpraševanje (na telefonu oba gumba pritrjena na dnu zaslona). Vozilo, ki ni več na
 * voljo, pokaže obvestilo, klic in zalogo.
 */
export function JavnoVozilo() {
  const { oznaka = '', id = '' } = useParams();
  const { search } = useLocation();
  const vgrajena = vgrajeno(search);
  const stran = useQuery({
    queryKey: ['javno-vozilo', oznaka, id],
    queryFn: ({ signal }) =>
      podatki(
        javniApi.GET('/api/javno/ponudba/{oznaka}/vozila/{id}', {
          signal,
          params: { path: { oznaka, id: Number(id) || 0 } },
        }),
      ),
  });
  useEffect(() => {
    if (!stran.data) {
      return;
    }
    const v = stran.data.vozilo;
    const prodajalec = stran.data.prodajalec?.naziv ?? '';
    document.title = `${v ? v.naziv : 'Vozilo ni več na voljo'} – ${prodajalec}`;
    return v ? undefined : prepreciIndeksiranje();
  }, [stran.data]);

  if (stran.isPending) {
    return (
      <Center h="100dvh">
        <Loader aria-label="Nalagam" />
      </Center>
    );
  }
  if (!stran.data) {
    return <NiPonudbe napaka={stran.error} ponovi={() => void stran.refetch()} />;
  }
  const { prodajalec = {}, vozilo } = stran.data;
  const zaloga = `/ponudba/${oznaka}${vgrajena ? '?vgrajeno=1' : ''}`;

  return (
    <PostavitevPonudbe prodajalec={prodajalec} vgrajena={vgrajena}>
      <Button component={Link} to={zaloga} variant="light" leftSection={<IconArrowLeft size={18} />} mb="md">
        Celotna zaloga
      </Button>
      {vozilo ? (
        <PodrobnostiVozila oznaka={oznaka} vozilo={vozilo} prodajalec={prodajalec} />
      ) : (
        <Paper radius="lg" p="xl" shadow="xs" maw={560} mx="auto">
          <Stack align="center" ta="center" gap="sm">
            <IconCar size={56} stroke={1.2} color={barve.besediloSekundarno} />
            <Title order={1} size="h3">
              Vozilo ni več na voljo
            </Title>
            <Text c="dimmed">To vozilo je že prodano ali ni več v ponudbi. Oglejte si druga vozila na zalogi.</Text>
            <Group justify="center" gap="sm" mt="xs">
              <Button component={Link} to={zaloga}>
                Oglejte si zalogo
              </Button>
              {prodajalec.telefon && (
                <Button
                  component="a"
                  href={telefonUrl(prodajalec.telefon)}
                  variant="light"
                  leftSection={<IconPhone size={18} />}
                >
                  Pokliči
                </Button>
              )}
            </Group>
          </Stack>
        </Paper>
      )}
    </PostavitevPonudbe>
  );
}

function PodrobnostiVozila({ oznaka, vozilo, prodajalec }: { oznaka: string; vozilo: Vozilo; prodajalec: Prodajalec }) {
  const obrazec = useRef<HTMLDivElement>(null);
  const naObrazec = () => {
    obrazec.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => obrazec.current?.querySelector('input')?.focus({ preventScroll: true }), 400);
  };
  return (
    <>
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
        <Galerija
          oznaka={oznaka}
          slike={vozilo.slike ?? []}
          slikeAi={vozilo.slikeAi ?? []}
          naziv={vozilo.naziv ?? ''}
        />
        <Stack gap="md">
          <div>
            <Title order={1} size="h2">
              {vozilo.naziv}
            </Title>
            {vozilo.povzetek && <Text c="dimmed">{vozilo.povzetek}</Text>}
          </div>
          <Group gap="sm" align="baseline">
            <Text fw={700} fz={32} c={barve.primarna} lh={1.1}>
              {vozilo.cena != null ? cenaOglasa(vozilo.cena) : 'Cena po dogovoru'}
            </Text>
            {vozilo.ddvIzkazan && (
              <Badge variant="light" size="lg">
                DDV izkazan
              </Badge>
            )}
          </Group>
          <Group gap="sm" visibleFrom="sm">
            {prodajalec.telefon && (
              <Button component="a" href={telefonUrl(prodajalec.telefon)} leftSection={<IconPhone size={18} />}>
                Pokliči {prodajalec.telefon}
              </Button>
            )}
            <Button variant="light" leftSection={<IconMail size={18} />} onClick={naObrazec}>
              Pošlji povpraševanje
            </Button>
          </Group>
          {(vozilo.podatki?.length ?? 0) > 0 && (
            <Kartica naslov="Podatki">
              <Podatki vrstice={(vozilo.podatki ?? []).map((p) => [p.oznaka ?? '', p.vrednost])} />
            </Kartica>
          )}
        </Stack>
      </SimpleGrid>
      {vozilo.opis && (
        <Box mt="lg">
          <Kartica naslov="Opis in oprema">
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {vozilo.opis}
            </Text>
          </Kartica>
        </Box>
      )}
      <Box mt="lg" ref={obrazec} style={{ scrollMarginTop: 16 }}>
        <Povprasevanje oznaka={oznaka} vozilo={vozilo} prodajalec={prodajalec} />
      </Box>
      {/* na telefonu klic in povpraševanje vedno na dosegu palca */}
      <Paper
        hiddenFrom="sm"
        shadow="md"
        p="sm"
        radius={0}
        style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 20 }}
      >
        <Group grow gap="xs">
          {prodajalec.telefon && (
            <Button component="a" href={telefonUrl(prodajalec.telefon)} leftSection={<IconPhone size={18} />} px="xs">
              Pokliči
            </Button>
          )}
          <Button variant="light" leftSection={<IconMail size={18} />} onClick={naObrazec} px="xs">
            Povpraševanje
          </Button>
        </Group>
      </Paper>
    </>
  );
}

/**
 * Velika slika s puščicami (na telefonu tudi poteg s prstom) in sličice pod njo; klik na sliko odpre celozaslonski
 * ogled ({@link Diaprojekcija}). Slike, urejene z umetno inteligenco, imajo oznako, če jo prodajalec želi
 * (Nastavitve → Urejanje slik).
 */
function Galerija({
  oznaka,
  slike,
  slikeAi,
  naziv,
}: {
  oznaka: string;
  slike: number[];
  slikeAi: number[];
  naziv: string;
}) {
  const [izbrana, setIzbrana] = useState(0);
  const [celozaslonsko, setCelozaslonsko] = useState(false);
  const pojdi = (smer: number) => setIzbrana((i) => (i + smer + slike.length) % slike.length);
  const poteg = usePoteg(pojdi);
  if (slike.length === 0) {
    return (
      <Center style={{ aspectRatio: '4 / 3', background: barve.izbrano, borderRadius: 20 }}>
        <IconCar size={80} stroke={1.1} color={barve.besediloSekundarno} />
      </Center>
    );
  }
  return (
    <Stack gap="xs">
      <Box
        pos="relative"
        style={{ aspectRatio: '4 / 3', background: barve.ozadjeTemno, borderRadius: 20, overflow: 'hidden' }}
        {...poteg}
      >
        <img
          src={slikaUrl(oznaka, slike[izbrana]!)}
          alt={`${naziv} – slika ${izbrana + 1} od ${slike.length}`}
          draggable={false}
          onClick={() => setCelozaslonsko(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
            userSelect: 'none',
            cursor: 'zoom-in',
          }}
        />
        <ActionIcon
          variant="white"
          radius="xl"
          size="lg"
          aria-label="Celozaslonski ogled"
          onClick={() => setCelozaslonsko(true)}
          style={{ position: 'absolute', right: 10, top: 10, opacity: 0.9 }}
        >
          <IconMaximize size={18} />
        </ActionIcon>
        {slike.length > 1 && (
          <>
            <ActionIcon
              variant="white"
              radius="xl"
              size="xl"
              aria-label="Prejšnja slika"
              onClick={() => pojdi(-1)}
              style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)' }}
            >
              <IconChevronLeft />
            </ActionIcon>
            <ActionIcon
              variant="white"
              radius="xl"
              size="xl"
              aria-label="Naslednja slika"
              onClick={() => pojdi(1)}
              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}
            >
              <IconChevronRight />
            </ActionIcon>
            <Badge variant="filled" color="dark" style={{ position: 'absolute', right: 10, bottom: 10, opacity: 0.8 }}>
              {izbrana + 1} / {slike.length}
            </Badge>
          </>
        )}
        {slikeAi.includes(slike[izbrana]!) && (
          <Badge
            variant="filled"
            color="dark"
            leftSection={<IconSparkles size={12} />}
            style={{ position: 'absolute', left: 10, bottom: 10, opacity: 0.8, textTransform: 'none' }}
          >
            Urejeno z AI
          </Badge>
        )}
      </Box>
      {slike.length > 1 && (
        <Group gap={6} wrap="nowrap" style={{ overflowX: 'auto', paddingBottom: 4 }}>
          {slike.map((s, i) => (
            <Box
              key={s}
              component="button"
              type="button"
              onClick={() => setIzbrana(i)}
              aria-label={`Slika ${i + 1}`}
              p={0}
              style={{
                border: `3px solid ${i === izbrana ? barve.poudarek : 'transparent'}`,
                borderRadius: 10,
                overflow: 'hidden',
                flexShrink: 0,
                width: 84,
                height: 63,
                cursor: 'pointer',
                background: barve.izbrano,
              }}
            >
              <img
                src={slikaUrl(oznaka, s, true)}
                alt=""
                loading="lazy"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </Box>
          ))}
        </Group>
      )}
      <Diaprojekcija
        odprta={celozaslonsko}
        zapri={() => setCelozaslonsko(false)}
        oznaka={oznaka}
        slike={slike}
        slikeAi={slikeAi}
        naziv={naziv}
        izbrana={izbrana}
        izberi={setIzbrana}
      />
    </Stack>
  );
}

/** Obrazec za povpraševanje: ime, telefon in/ali e-pošta, sporočilo; napake ob poljih s strežnika. */
function Povprasevanje({ oznaka, vozilo, prodajalec }: { oznaka: string; vozilo: Vozilo; prodajalec: Prodajalec }) {
  const [ime, setIme] = useState('');
  const [telefon, setTelefon] = useState('');
  const [email, setEmail] = useState('');
  const [sporocilo_, setSporocilo] = useState(`Pozdravljeni, zanima me vozilo ${vozilo.naziv}. Ali je še na voljo?`);
  const [spletnaStran, setSpletnaStran] = useState('');
  const [polja, setPolja] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const [poslano, setPoslano] = useState(false);

  const oddaj = async (e: FormEvent) => {
    e.preventDefault();
    setPolja({});
    setNapaka(null);
    setDela(true);
    try {
      await podatki(
        javniApi.POST('/api/javno/ponudba/{oznaka}/povprasevanja', {
          params: { path: { oznaka } },
          body: { voziloId: vozilo.id, ime, telefon, email, sporocilo: sporocilo_, spletnaStran },
        }),
      );
      setPoslano(true);
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(false);
    }
  };

  if (poslano) {
    return (
      <Kartica>
        <Stack align="center" ta="center" gap="xs" py="md">
          <IconCircleCheck size={48} color={barve.primarna} />
          <Title order={2} size="h3">
            Hvala, povpraševanje je poslano
          </Title>
          <Text c="dimmed" maw={480}>
            {prodajalec.naziv} vas bo kontaktiral čim prej.
            {prodajalec.telefon ? ` Če se vam mudi, pokličite ${prodajalec.telefon}.` : ''}
          </Text>
        </Stack>
      </Kartica>
    );
  }

  return (
    <Kartica naslov="Pošlji povpraševanje">
      <form onSubmit={(e) => void oddaj(e)} noValidate>
        <Stack gap="sm">
          <TextInput
            label="Ime in priimek"
            value={ime}
            onChange={(e) => setIme(e.currentTarget.value)}
            error={polja.ime}
            autoComplete="name"
            withAsterisk
          />
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput
              label="Telefon"
              type="tel"
              value={telefon}
              onChange={(e) => setTelefon(e.currentTarget.value)}
              error={polja.telefon}
              autoComplete="tel"
              inputMode="tel"
            />
            <TextInput
              label="E-pošta"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              error={polja.email}
              autoComplete="email"
            />
          </SimpleGrid>
          <Text size="xs" c="dimmed" mt={-4}>
            Vpišite telefon ali e-pošto, da vas prodajalec lahko pokliče ali vam odgovori.
          </Text>
          <Textarea
            label="Sporočilo"
            value={sporocilo_}
            onChange={(e) => setSporocilo(e.currentTarget.value)}
            error={polja.sporocilo}
            autosize
            minRows={3}
            maxRows={10}
            withAsterisk
          />
          {/* skrito polje proti botom: ljudje ga ne vidijo in ne izpolnijo */}
          <input
            type="text"
            name="spletnaStran"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={spletnaStran}
            onChange={(e) => setSpletnaStran(e.currentTarget.value)}
            style={{ position: 'absolute', left: -10000, width: 1, height: 1, opacity: 0 }}
          />
          {napaka && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} role="alert">
              {napaka}
            </Alert>
          )}
          <Button type="submit" loading={dela} leftSection={<IconMail size={18} />} size="lg">
            Pošlji povpraševanje
          </Button>
          <Text size="xs" c="dimmed">
            Podatke prejme samo {prodajalec.naziv}, da vam odgovori na povpraševanje.
          </Text>
        </Stack>
      </form>
    </Kartica>
  );
}
